import { zodResolver } from "@hookform/resolvers/zod";
import type { JobOpening } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Briefcase, Building2, CheckCircle2, DollarSign, Paperclip, Send, X } from "lucide-react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ThemeToggle } from "@/components/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { orpc, rpcClient } from "@/lib/rpc";

const applicationFormSchema = z.object({
  applicantName: z.string().min(2, "Nome é obrigatório"),
  applicantEmail: z.string().email("Email inválido"),
  applicantPhone: z.string().optional(),
  linkedinUrl: z.string().url("URL inválida").optional().or(z.literal("")),
});

type ApplicationFormValues = z.infer<typeof applicationFormSchema>;

export default function Careers() {
  const [selectedJob, setSelectedJob] = useState<JobOpening | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const { data: jobs, isLoading } = useQuery(orpc.jobOpenings.listPublic.queryOptions()) as {
    data: JobOpening[] | undefined;
    isLoading: boolean;
  };

  const form = useForm<ApplicationFormValues>({
    resolver: zodResolver(applicationFormSchema),
    defaultValues: {
      applicantName: "",
      applicantEmail: "",
      applicantPhone: "",
      linkedinUrl: "",
    },
  });

  const applyMutation = useMutation({
    mutationFn: async (data: ApplicationFormValues & { jobOpeningId: string }) => {
      return rpcClient.applications.create({
        applicantName: data.applicantName,
        applicantEmail: data.applicantEmail,
        applicantPhone: data.applicantPhone,
        linkedinUrl: data.linkedinUrl,
        jobOpeningId: data.jobOpeningId,
        type: "organic",
      });
    },
    onSuccess: () => {
      setIsDialogOpen(false);
      setIsSuccessOpen(true);
      form.reset();
      setResumeFile(null);
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível enviar sua candidatura. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ApplicationFormValues) => {
    if (!selectedJob) return;
    applyMutation.mutate({
      ...data,
      jobOpeningId: selectedJob.id,
    });
  };

  const handleApplyClick = (job: JobOpening) => {
    setSelectedJob(job);
    setIsDialogOpen(true);
  };

  const formatSalary = (salary: string | null) => {
    if (!salary) return null;
    const value = parseFloat(salary);
    if (Number.isNaN(value)) return null;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getLevelLabel = (level: string) => {
    const labels: Record<string, string> = {
      L0: "Estágio",
      L1: "Júnior",
      L2: "Pleno",
      L3: "Sênior",
      L4: "Especialista",
      L5: "Principal",
      L6: "Staff",
      L7: "Diretor",
    };
    return labels[level] || level;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Carreiras</h1>
            <p className="text-muted-foreground">Junte-se ao nosso time</p>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-2">Vagas Abertas</h2>
          <p className="text-muted-foreground">
            Confira nossas oportunidades e encontre a vaga ideal para você
          </p>
        </div>

        {isLoading ? (
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-6 bg-muted rounded w-1/3" />
                  <div className="h-4 bg-muted rounded w-1/4 mt-2" />
                </CardHeader>
                <CardContent>
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-2/3 mt-2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : jobs && jobs.length > 0 ? (
          <div className="grid gap-4">
            {jobs.map((job) => (
              <Card
                key={job.id}
                className="hover-elevate transition-all"
                data-testid={`card-job-${job.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle className="text-lg">{job.title}</CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <Building2 className="h-4 w-4" />
                        {job.department}
                      </CardDescription>
                    </div>
                    <Button
                      onClick={() => handleApplyClick(job)}
                      data-testid={`button-apply-${job.id}`}
                    >
                      Candidatar-se
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Briefcase className="h-3 w-3" />
                      {getLevelLabel(job.jobLevel)}
                    </Badge>
                    {formatSalary(job.salaryExpected) && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        {formatSalary(job.salaryExpected)}
                      </Badge>
                    )}
                  </div>
                  {job.generalResponsibilities && (
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {job.generalResponsibilities}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Briefcase className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Nenhuma vaga disponível</h3>
              <p className="text-muted-foreground text-center">
                No momento não temos vagas abertas, mas volte em breve!
              </p>
            </CardContent>
          </Card>
        )}
      </main>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Candidatar-se para {selectedJob?.title}</DialogTitle>
            <DialogDescription>
              Preencha seus dados para se candidatar a esta vaga
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="applicantName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome Completo *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Seu nome completo"
                        {...field}
                        data-testid="input-applicant-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="applicantEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="seu@email.com"
                        {...field}
                        data-testid="input-applicant-email"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="applicantPhone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefone</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="(11) 99999-9999"
                        {...field}
                        data-testid="input-applicant-phone"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="linkedinUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://linkedin.com/in/seu-perfil"
                        {...field}
                        data-testid="input-linkedin-url"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div>
                <label htmlFor="resume-file" className="text-sm font-medium">
                  Currículo (PDF, DOC, DOCX)
                </label>
                <div className="mt-1.5">
                  <input
                    id="resume-file"
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    data-testid="input-resume-file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 10 * 1024 * 1024) {
                          toast({
                            title: "Erro",
                            description: "Arquivo muito grande. Máximo 10MB.",
                            variant: "destructive",
                          });
                          return;
                        }
                        setResumeFile(file);
                      }
                    }}
                  />
                  {resumeFile ? (
                    <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
                      <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm truncate flex-1" data-testid="text-resume-filename">
                        {resumeFile.name}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          setResumeFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                        data-testid="button-remove-resume"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="button-attach-resume"
                    >
                      <Paperclip className="h-4 w-4 mr-2" />
                      Anexar Currículo
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  data-testid="button-cancel-application"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={applyMutation.isPending}
                  data-testid="button-submit-application"
                >
                  {applyMutation.isPending ? (
                    "Enviando..."
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Enviar Candidatura
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={isSuccessOpen} onOpenChange={setIsSuccessOpen}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center text-center py-4">
            <CheckCircle2 className="h-16 w-16 text-[hsl(var(--forest-pulse))] mb-4" />
            <DialogTitle className="text-xl mb-2">Candidatura Enviada!</DialogTitle>
            <DialogDescription className="text-base">
              Recebemos sua candidatura para a vaga de{" "}
              <span className="font-medium text-foreground">{selectedJob?.title}</span>.
              <br />
              Nossa equipe entrará em contato em breve.
            </DialogDescription>
            <Button
              className="mt-6"
              onClick={() => {
                setIsSuccessOpen(false);
                setSelectedJob(null);
              }}
              data-testid="button-close-success"
            >
              Voltar às Vagas
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
